// src/App.js
import './App.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import CardComponent from './components/CardComponent';
import ChartComponent from './components/ChartComponent';
import AgingTable from './components/AgingTable';
import Papa from 'papaparse';
import logo from './logo-main.gif';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './components/Login';

const initialAgingData = {
  ar: {
    date: '',
    days0to30: 0,
    days31to60: 0,
    days61to90: 0,
    daysOver90: 0,
    total: 0
  },
  ap: {
    date: '',
    days0to30: 0,
    days31to60: 0,
    days61to90: 0,
    daysOver90: 0,
    total: 0
  }
};

const Dashboard = () => {
    const { logout } = useAuth();
    const [selectedDate, setSelectedDate] = useState(() => {
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return yesterday.toISOString().split('T')[0];
    });
    const [data, setData] = useState([]);
    const [availableDates, setAvailableDates] = useState([]);
    const [currentData, setCurrentData] = useState(null);
    const [chartData, setChartData] = useState(null);
    const [activeMetrics, setActiveMetrics] = useState(['Wholesale Sales', 'Retail Sales']);
    const [dateRange, setDateRange] = useState(30);
    const [showChart, setShowChart] = useState(true);
    const [arAgingData, setArAgingData] = useState(initialAgingData.ar);
    const [apAgingData, setApAgingData] = useState(initialAgingData.ap);

    useEffect(() => {
        const loadData = async () => {
            try {
                const response = await fetch('/data.csv');
                if (!response.ok) {
                    throw new Error('Failed to fetch data');
                }
                const csvText = await response.text();
                const parsedData = Papa.parse(csvText, {
                    header: true,
                    skipEmptyLines: true,
                    dynamicTyping: true
                }).data;

                if (parsedData.length > 0) {
                    const selectedRowData = parsedData.find(row => row.Date === selectedDate) || parsedData[0];

                    const processValue = (value) => {
                        if (!value || value === ' - ') return 0;
                        return parseFloat(String(value).replace(/[$,]/g, '')) || 0;
                    };

                    setArAgingData({
                        days0to30: processValue(selectedRowData['ar_0-30']),
                        days31to60: processValue(selectedRowData['ar_31-60']),
                        days61to90: processValue(selectedRowData['ar_61-90']),
                        daysOver90: processValue(selectedRowData['ar_91+']),
                        total: processValue(selectedRowData['ar_total'])
                    });

                    setApAgingData({
                        days0to30: processValue(selectedRowData['ap_0-30']),
                        days31to60: processValue(selectedRowData['ap_31-60']),
                        days61to90: processValue(selectedRowData['ap_61-90']),
                        daysOver90: processValue(selectedRowData['ap_91+']),
                        total: processValue(selectedRowData['ap_total'])
                    });
                }

                const validData = parsedData
                    .filter(row => row['Daily Total'] && row['Daily Total'] !== ' - ')
                    .sort((a, b) => new Date(b.Date) - new Date(a.Date));

                setData(validData);
                const dates = validData.map(row => row.Date);
                setAvailableDates(dates);

                if (validData.length > 0) {
                    const defaultDate = validData.find(row => {
                        const rowDate = new Date(row.Date);
                        return rowDate <= new Date();
                    })?.Date || validData[0].Date;

                    setSelectedDate(defaultDate);
                    setCurrentData(validData.find(row => row.Date === defaultDate));
                    updateChartData(defaultDate, ['Wholesale Sales', 'Retail Sales']);
                }
            } catch (error) {
                console.error('Error loading data:', error);
            }
        };

        loadData();
    }, []);

    useEffect(() => {
        const selectedRow = data.find(row => row.Date === selectedDate);
        if (selectedRow) {
            setCurrentData(selectedRow);

            const processValue = (value) => {
                if (!value || value === ' - ') return 0;
                return parseFloat(String(value).replace(/[$,]/g, '')) || 0;
            };

            setArAgingData({
                days0to30: processValue(selectedRow['ar_0-30']),
                days31to60: processValue(selectedRow['ar_31-60']),
                days61to90: processValue(selectedRow['ar_61-90']),
                daysOver90: processValue(selectedRow['ar_91+']),
                total: processValue(selectedRow['ar_total'])
            });

            setApAgingData({
                days0to30: processValue(selectedRow['ap_0-30']),
                days31to60: processValue(selectedRow['ap_31-60']),
                days61to90: processValue(selectedRow['ap_61-90']),
                daysOver90: processValue(selectedRow['ap_91+']),
                total: processValue(selectedRow['ap_total'])
            });
        }
        updateChartData(selectedDate, activeMetrics);
    }, [selectedDate, data, activeMetrics, dateRange]);

    const handleDateChange = (event) => {
        setSelectedDate(event.target.value);
    };

    const handleDateRangeChange = (event) => {
        setDateRange(parseInt(event.target.value));
    };

    const toggleMetric = (title) => {
        setActiveMetrics(prev => {
            if (prev.includes(title)) {
                return prev.filter(t => t !== title);
            } else {
                return [...prev, title];
            }
        });
    };

    const isWeekend = (dateString) => {
        const date = new Date(dateString);
        const day = date.getDay();
        return day === 0 || day === 6;
    };

    const formatDateOption = (date) => {
        const isWeekendDate = isWeekend(date);
        return {
            value: date,
            label: date,
            style: isWeekendDate ? { color: '#999', fontStyle: 'italic' } : {}
        };
    };

    const getMetricColor = (index) => {
        const colors = [
            'rgb(75, 192, 192)',
            'rgb(255, 99, 132)',
            'rgb(54, 162, 235)',
            'rgb(255, 206, 86)',
            'rgb(153, 102, 255)',
            'rgb(255, 159, 64)',
            'rgb(75, 192, 192)',
            'rgb(255, 99, 132)'
        ];
        return colors[index % colors.length];
    };

    const updateChartData = (date, metrics) => {
        const selectedDateIndex = data.findIndex(row => row.Date === date);
        const rangeData = data
            .slice(selectedDateIndex, selectedDateIndex + dateRange)
            .sort((a, b) => new Date(a.Date) - new Date(b.Date));

        const processValue = (value) => {
            if (!value || value === ' - ') return 0;
            const cleanValue = value.toString().replace(/[$,]/g, '');
            return parseFloat(cleanValue) || 0;
        };

        const metricMappings = {
            'Wholesale Sales': 'Actual Sales Wholesale',
            'Retail Sales': 'Actual Sales Retail',
            'Daily Total': 'Daily Total',
            'MTD': 'MTD',
            'YTD': 'YTD',
            'Difference - Actual: Budget YTD +/(-)': 'difference - actual:budget YTD +/(-)',
            'Difference - Actual: Budget MTD +/(-)': 'difference - actual:budget MTD +/(-)',
            'Cash Balance': 'Bank Balance',
            'AR': 'Accounts Receivable',
            'Inventory Value': 'Inventory',
            'AP': 'Accounts Payable'
        };

        const chartData = {
            labels: rangeData.map(row => row.Date),
            datasets: metrics.map((metric, index) => ({
                label: metric,
                data: rangeData.map(row => processValue(row[metricMappings[metric] || metric])),
                fill: false,
                backgroundColor: getMetricColor(index),
                borderColor: getMetricColor(index),
                tension: 0.1
            }))
        };
        setChartData(chartData);
    };

    if (!currentData) {
        return <div>Loading...</div>;
    }

    const allCardData = [
        { title: 'Wholesale Sales', value: currentData['Actual Sales Wholesale'] || '0' },
        { title: 'Retail Sales', value: currentData['Actual Sales Retail'] || '0' },
        { title: 'Daily Total', value: currentData['Daily Total'] },
        { title: 'MTD', value: currentData['MTD'] },
        { title: 'YTD', value: currentData['YTD'] },
        { title: 'Cash Balance', value: currentData['Bank Balance'] },
        { title: 'AR', value: currentData['Accounts Receivable'] },
        { title: 'Inventory Value', value: currentData['Inventory'] },
        { title: 'AP', value: currentData['Accounts Payable'] }
    ];

    const chartOptions = {
        maintainAspectRatio: false,
        scales: {
            y: {
                beginAtZero: false,
                ticks: {
                    callback: (value) => {
                        return new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                        }).format(value);
                    }
                }
            }
        },
        plugins: {
            tooltip: {
                callbacks: {
                    label: (context) => {
                        return `${context.dataset.label}: ${new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                        }).format(context.raw)}`;
                    }
                }
            }
        }
    };

    return (
        <Container>
            <div className="header-container">
                <div>
                    <img src={logo} alt="LMS Logo" className="logo" />
                </div>
                <div className="controls-container">
                    <div className="d-flex align-items-center gap-2">
                        <label htmlFor="date-selector">Select Date:</label>
                        <select
                            id="date-selector"
                            value={selectedDate}
                            onChange={handleDateChange}
                            className="form-select"
                        >
                            {availableDates.map(date => {
                                const { value, label, style } = formatDateOption(date);
                                return (
                                    <option
                                        key={value}
                                        value={value}
                                        style={style}
                                    >
                                        {label} {isWeekend(date) ? '(Weekend)' : ''}
                                    </option>
                                );
                            })}
                        </select>
                    </div>
                    {showChart && (
                        <div className="d-flex align-items-center gap-2">
                            <label htmlFor="range-selector">Date Range:</label>
                            <select
                                id="range-selector"
                                value={dateRange}
                                onChange={handleDateRangeChange}
                                className="form-select"
                            >
                                <option value={30}>30 Days</option>
                                <option value={60}>60 Days</option>
                                <option value={90}>90 Days</option>
                            </select>
                        </div>
                    )}
                    <button
                        onClick={() => setShowChart(!showChart)}
                        className={`toggle-button ${showChart ? 'active' : ''}`}
                    >
                        <span className="material-icons">
                            {showChart ? 'visibility_off' : 'visibility'}
                        </span>
                        {showChart ? 'Hide Chart' : 'Show Chart'}
                    </button>
                    <button
                        onClick={logout}
                        className="btn btn-outline-danger ms-2"
                    >
                        Logout
                    </button>
                </div>
            </div>
            {showChart && (
                <Row className="mt-3 mb-4">
                    <Col md={12} style={{ height: '400px' }}>
                        {chartData && <ChartComponent data={chartData} options={chartOptions} />}
                    </Col>
                </Row>
            )}
            <Row className="mt-3">
                {allCardData.map((item, index) => (
                    <Col key={index} xs={12} sm={6} md={4} lg={2} className="mb-3">
                        <CardComponent
                            title={item.title}
                            value={item.value}
                            onChartClick={() => toggleMetric(item.title)}
                            isActive={activeMetrics.includes(item.title)}
                        />
                    </Col>
                ))}
            </Row>
            <Row className="mt-4">
                <Col md={6}>
                    <AgingTable
                        title="Accounts Receivable Aging Summary"
                        data={{
                            days0to30: arAgingData.days0to30,
                            days31to60: arAgingData.days31to60,
                            days61to90: arAgingData.days61to90,
                            daysOver90: arAgingData.daysOver90,
                            total: arAgingData.total
                        }}
                    />
                </Col>
                <Col md={6}>
                    <AgingTable
                        title="Accounts Payable Aging Summary"
                        data={{
                            days0to30: apAgingData.days0to30,
                            days31to60: apAgingData.days31to60,
                            days61to90: apAgingData.days61to90,
                            daysOver90: apAgingData.daysOver90,
                            total: apAgingData.total
                        }}
                    />
                </Col>
            </Row>
        </Container>
    );
};

function AppContent() {
    const { isAuthenticated } = useAuth();

    return isAuthenticated ? <Dashboard /> : <Login />;
}

function App() {
    return (
        <AuthProvider>
            <AppContent />
        </AuthProvider>
    );
}

export default App;